import { Component } from '@theme/component'
import {
  RawPreloaderReadyEvent,
  rawPreloaderReadyClassName,
  scrollLock
} from '@theme/events'
import { loadGsapPlugins } from '@theme/gsap'
import { mediaQueryLarge } from '@theme/use-device-detection'

const PreloaderConfig = {
  fallbackMs: 4200,
  raw: {
    durationIn: 0.1,
    durationOut: 0.2,
    ease: 'back.out(1.7)',
    fadeOutDelay: 1.2,
    fadeOutDuration: 0.2,
    overlapOut: '-=0.4',
    scaleIn: 1.3,
    shakeDuration: 0.05,
    shakeRepeat: 2,
    shakeX: 2,
    shakeY: 2,
    staggerEach: 0.12,
    staggerOut: 0.1
  },
  reducedMotionFadeDuration: 0.2,
  scribble: {
    width: '31%',
    widthStart: '8%'
  }
}

const reducedMotionQuery = matchMedia('(prefers-reduced-motion: reduce)')
const rawPreloaderSelector = 'raw-preloader'
let rawPreloaderReadyDispatched = false

function markRawPreloaderReady(detail = {}) {
  if (rawPreloaderReadyDispatched) return

  rawPreloaderReadyDispatched = true
  document.documentElement.classList.add(
    'raw-preloader-run',
    rawPreloaderReadyClassName
  )
  document.dispatchEvent(new RawPreloaderReadyEvent(detail))
}

function removeRawPreloaderShell() {
  for (const element of document.querySelectorAll(rawPreloaderSelector)) {
    element.remove()
  }
}

class RawPreloader extends Component {
  requiredRefs = ['rawText', 'screen', 'scribblePath']

  #fallbackTimer = null
  #gsapContext = null
  #isReleased = false
  #timeline = null

  mounted() {
    if (!mediaQueryLarge.matches) {
      markRawPreloaderReady({
        desktop: false,
        reason: 'mobile',
        skipped: true
      })
      this.remove()
      return
    }

    let hasRun = false
    try {
      hasRun = sessionStorage.getItem('raw-preloader-run') === 'true'
      // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional no-op
    } catch {}

    if (hasRun) {
      this.#releaseImmediately('session')
      return
    }

    this.dataset.rawPreloaderActive = ''

    try {
      sessionStorage.setItem('raw-preloader-run', 'true')
      // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional no-op
    } catch {}

    this.#fallbackTimer = setTimeout(() => {
      this.#release('timeout')
    }, PreloaderConfig.fallbackMs)

    void this.#play()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    clearTimeout(this.#fallbackTimer)
    this.#timeline?.kill()
    this.#gsapContext?.revert()
    scrollLock(false, 'preloader')
  }

  async #play() {
    if (this.#isReleased) return

    scrollLock(true, 'preloader')

    if (reducedMotionQuery.matches) {
      this.#playReducedMotion()
      return
    }

    const gsap = await loadGsapPlugins(['CustomEase', 'DrawSVGPlugin'])
    if (
      !(
        gsap &&
        globalThis.CustomEase &&
        globalThis.DrawSVGPlugin &&
        this.isConnected
      )
    ) {
      this.#release('missing-gsap')
      return
    }

    globalThis.CustomEase?.create(
      'raw-preloader-scribble',
      '0.75, 0.15, 0.15, 1'
    )

    this.#gsapContext = gsap.context(() => {
      this.#buildCharacters()
      this.#timeline = this.#buildTimeline(gsap)
    }, this)
  }

  #playReducedMotion() {
    const { gsap } = window
    if (!gsap) {
      this.#release()
      return
    }

    this.#timeline = gsap.timeline({
      onComplete: () => this.#release()
    })
    this.#timeline.to(this, {
      autoAlpha: 0,
      duration: PreloaderConfig.reducedMotionFadeDuration,
      ease: 'none'
    })
  }

  #buildCharacters() {
    const target = this.refs.rawText
    if (!(target instanceof HTMLElement) || target.children.length > 0) {
      return
    }

    const text = this.dataset.loaderText || 'RAAAAAW!'
    for (const char of text) {
      const span = document.createElement('span')
      span.className = 'raw-preloader__glyph'
      span.textContent = char
      target.append(span)
    }
  }

  #buildTimeline(gsap) {
    const rawChars = Array.from(this.querySelectorAll('.raw-preloader__glyph'))
    const rawLayer = this.refs.rawLayer
    const rawTrigger = this.refs.rawTrigger
    const scribblePath = this.refs.scribblePath
    const screen = this.refs.screen
    const raw = PreloaderConfig.raw
    const hasRawSegment = rawLayer instanceof HTMLElement && rawChars.length > 0

    const shake = () => {
      if (!(rawTrigger instanceof HTMLElement)) return

      gsap.fromTo(
        rawTrigger,
        {
          x: -raw.shakeX,
          y: -raw.shakeY
        },
        {
          clearProps: 'x,y',
          duration: raw.shakeDuration,
          repeat: raw.shakeRepeat,
          x: raw.shakeX,
          y: raw.shakeY,
          yoyo: true
        }
      )
    }

    const timeline = gsap.timeline({
      onComplete: () => this.#release(),
      onInterrupt: () => this.#release()
    })

    timeline.set(this, { pointerEvents: 'auto' })
    timeline.set(screen, { autoAlpha: 0.2 })
    timeline.set(scribblePath, {
      drawSVG: '0% 100%',
      strokeWidth: PreloaderConfig.scribble.width
    })
    timeline.set(rawLayer, { autoAlpha: hasRawSegment ? 1 : 0 })

    if (hasRawSegment) {
      timeline.set(rawChars, { clearProps: 'transform', opacity: 0, scale: 1 })
      timeline.to(rawChars, {
        duration: raw.durationIn,
        ease: raw.ease,
        opacity: 1,
        scale: raw.scaleIn,
        stagger: {
          each: raw.staggerEach,
          onStart: shake
        }
      })
      timeline.to(
        rawChars,
        {
          duration: raw.durationOut,
          scale: 1,
          stagger: raw.staggerOut
        },
        raw.overlapOut
      )
      timeline.add('svg')
      timeline.to(
        rawLayer,
        {
          autoAlpha: 0,
          duration: raw.fadeOutDuration,
          ease: 'none'
        },
        `svg+=${raw.fadeOutDelay}`
      )
    } else {
      timeline.add('svg', 0)
    }

    timeline.to(
      scribblePath,
      {
        drawSVG: '100% 100%',
        duration: 2,
        ease: 'power2.inOut'
      },
      'svg+=0.25'
    )
    timeline.to(
      screen,
      {
        autoAlpha: 0,
        duration: 2,
        ease: 'none'
      },
      'svg+=0.25'
    )
    timeline.to(
      scribblePath,
      {
        duration: 2,
        ease: 'raw-preloader-scribble',
        strokeWidth: PreloaderConfig.scribble.widthStart
      },
      'svg+=0.25'
    )
    timeline.set(scribblePath, {
      drawSVG: '0% 0%',
      strokeWidth: '0%'
    })
    timeline.to(
      this,
      {
        autoAlpha: 0,
        duration: 0.2,
        ease: 'none'
      },
      '>-0.1'
    )

    return timeline
  }

  #release(reason = 'complete') {
    if (this.#isReleased) return
    this.#isReleased = true

    clearTimeout(this.#fallbackTimer)
    scrollLock(false, 'preloader')
    this.hidden = true
    this.remove()

    try {
      markRawPreloaderReady({
        desktop: true,
        reason,
        skipped: false
      })
      // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional no-op
    } catch {}
  }

  #releaseImmediately(reason = 'session') {
    this.#isReleased = true
    this.hidden = true
    this.remove()

    try {
      markRawPreloaderReady({
        desktop: mediaQueryLarge.matches,
        reason,
        skipped: true
      })
      // biome-ignore lint/suspicious/noEmptyBlockStatements: intentional no-op
    } catch {}
  }
}

if (!mediaQueryLarge.matches) {
  markRawPreloaderReady({
    desktop: false,
    reason: 'mobile',
    skipped: true
  })
  removeRawPreloaderShell()
} else if (!customElements.get('raw-preloader')) {
  customElements.define('raw-preloader', RawPreloader)
}
